/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import IMS from './IMS';
import Url from '../../utils/Url';
import { setCookieWithDomain, setCookie, getCookie, deleteCookie } from '../../utils/cookie';
import ArrayUtils from '../../utils/ArrayUtils';
import EntitlementService from './EntitlementService';
import FloodgateService from './FloodgateService';
import ProfileService from './ProfileService';
import FeaturesService from './FeaturesService';
import SearchAppService from './SearchAppService';
import { getAppLocale } from '@intl/adobe-locales';
import getConfig from '../../config';
import { PIIP } from './PIIP';
import PostIMSFetchService from './PostIMSFetchService';

const initialState: genericObject = {};
const REFERRER_KEY = 'creative-cloud-referrer';

class BootstrapService {
	constructor() {
		deleteCookie('cchome-loggedIn');

		this.loadIngestScriptFiles();
		this.loadYoutubeIFrameScriptFile();
	}

	loadIngestScriptFiles() {
		const body = document.getElementsByTagName('body')[0];
		const visitorAPIElements = document.createElement('script');
		visitorAPIElements.type = 'text/javascript';
		visitorAPIElements.src = window.chash ? `/${window.chash}/VisitorAPI.js` : '/VisitorAPI.js';
		body.appendChild(visitorAPIElements);
		visitorAPIElements.onerror = function () {
			console.warn('Error loading', this.src);
		};
	}

	/**
	 * Load in the youtube iframe api
	 */
	loadYoutubeIFrameScriptFile() {
		const script = document.createElement('script');
		script.src = 'https://www.youtube.com/iframe_api';
		script.id = 'youtubeIframeApi';
		document.body.appendChild(script);
	}

	getInitialState(progressCallback, store, isCCD?: boolean) {
		return new Promise((resolve, reject) => {
			// getAppLocale's 3rd argument is the fallback if the language in our store is not accepted by IMS
			const lang = getAppLocale('ims', store.getState().app.language, 'en');
			IMS.configImsService(lang, isCCD);
			// If IMS returns a non-null profile, we consider the user signed-in and continue loading CC Home.
			// If IMS returns an error, will pass on the error to client.js via a Promise rejection and this will result
			// in the user seeing an error page.
			if (
				window.location &&
				(window.location.href === 'https://dev.hollywoodstudios.corp.adobe.com:9000/' ||
					window.location.href === 'https://dev.hollywoodstudios.corp.adobe.com:9000')
			) {
				window.location.assign('https://dev.hollywoodstudios.corp.adobe.com:9000/cc');
			}

			Promise.all([Promise.race([IMS.profile, IMS.error]), FeaturesService.fetchFlags(store, true)])
				.then(([profile]) => {
					setCookieWithDomain('cchome-loggedIn', profile && (profile.authId || profile.userId));
					window.cchome.config = { features: [] };

					// Feature flag query parameters - Turns on or off feature flags specified in query param.

					// "addfeatures" query parameter
					// Usage: Use comma delimited list of feature flags to turn them ON:
					// https://dev-uw1.cchome.adobe.com?addfeatures=temp_feature_1,temp_feature_2

					const addFeaturesParam = Url.getParam('addfeatures');
					const addFeaturesCookie = getCookie('cchome-addff');

					// Make it a set so there are no duplicates
					const featuresToAdd = new Set<string>();

					// Add requested add features to new features array
					const addFeaturesParamArray = addFeaturesParam?.split(',') || [];
					addFeaturesParamArray.forEach((feature: string) => featuresToAdd.add(feature));

					const addFeaturesCookieArray = addFeaturesCookie?.split(',') || [];
					addFeaturesCookieArray.forEach((feature: string) => featuresToAdd.add(feature));

					window.cchome.util.addFeaturesArray = [...featuresToAdd];

					if (addFeaturesParam) {
						setCookieWithDomain('cchome-addff', window.cchome.util.addFeaturesArray.join(), 1);
					}

					// "remfeatures" query parameter
					// Usage: Use comma delimited list of feature flags to turn them OFF:
					// Feature flags will stay turned off ACROSS feature sets
					// for an hour after first set or until cleared with a blank param (e.g. /plans?remfeatures=).
					// NOTE: remfeatures takes precedence over addfeatures regardless of whether it is loaded from the cookie
					// or the parameter.

					const remFeaturesParam = Url.getParam('remfeatures');
					const remFeaturesCookie = getCookie('cchome-remff');

					// Make it a set so there are no duplicates
					const featuresToRem = new Set<string>();

					// Remove requested features from new features array
					const remFeaturesParamArray = remFeaturesParam?.split(',') || [];
					remFeaturesParamArray.forEach((feature: string) => featuresToRem.add(feature));

					const remFeaturesCookieArray = remFeaturesCookie?.split(',') || [];
					remFeaturesCookieArray.forEach((feature: string) => featuresToRem.add(feature));

					window.cchome.util.removeFeaturesArray = [...featuresToRem];

					if (remFeaturesParam) {
						setCookieWithDomain('cchome-remff', window.cchome.util.removeFeaturesArray.join(), 1);
					}

					const finalFeatures = featuresToAdd;
					featuresToRem.forEach((feature: string) => finalFeatures.delete(feature));

					window.cchome.config.features = [...new Set([...window.cchome.config.features, ...finalFeatures])];

					if (progressCallback) {
						progressCallback(100 / 2);
					}

					if (
						window.cchome.util.hasFeature('temp_enable_cchome_apps_logs') &&
						window.location.pathname.indexOf('/apps') === 0
					) {
						console.time('page_load_time');
					}
					initialState.signedIn = IMS.isSignedInUser(false);
					initialState.profile = profile;
					if (progressCallback) {
						progressCallback(2 * (100 / 2));
					}

					const promiseArr: Array<Promise<any> | boolean> = [];
					if (isCCD) {
						const ccdPromise = new Promise((resolve) => {
							resolve(true);
						});
						promiseArr.push(ccdPromise);
					} else {
						const isUserEligibleForCCHomePromise = new EntitlementService().isUserEligibleForCCHome(
							store.getState().app.language,
						);
						promiseArr.push(isUserEligibleForCCHomePromise);
					}
					// Not waiting for user to sign-in before fetching data.
					// Handling can be added on client side
					// if a call depends on IMS token.
					PostIMSFetchService.registerServices(store.getState().app.language);
					PostIMSFetchService.fetchData();
					return Promise.all(promiseArr);
				})
				.then(([isUserEligibleForCCHome]) => {
					const piip = new PIIP(
						getConfig('PIIP').host,
						window.adobeIMS && window.adobeIMS.getAccessToken(),
						window.adobeIMS && window.adobeIMS.getClientID(),
					);
					piip.isOptedOutOfAnalytics().then((optedOut) => {
						window.cchome.util.optOutAnalytics = optedOut;
						window.cchome.util.optOutAnalyticsInited = true;

						// Send the analtyics that were sent before we got
						// a response from PIIP
						const customEvent = new CustomEvent('analyticsOptinInited');
						window.dispatchEvent(customEvent);
					});
					// If we had a referrer before SUSI redirect, keep it. Otherwise, it's null.
					const originalReferrerCookie = getCookie(REFERRER_KEY) || null;
					// Set the referrer cookie before we redirect to SUSI login, otherwise referrer will be adobelogin.com URL
					const newReferrerCookieVal =
						document.referrer.indexOf('/ims') === -1 ? document.referrer : originalReferrerCookie;
					setCookie(REFERRER_KEY, newReferrerCookieVal);
					if (window.cchome.util.storageAvailable('sessionStorage')) {
						const originalSessionStorageVal = JSON.parse(sessionStorage.getItem(REFERRER_KEY) || '{}');
						const originalReferrerSessionVal =
							(originalSessionStorageVal && originalSessionStorageVal.referrer) || null;
						const newReferrerSessionVal =
							document.referrer.indexOf('/ims') === -1 ? document.referrer : originalReferrerSessionVal;
						sessionStorage.setItem(REFERRER_KEY, JSON.stringify({ referrer: newReferrerSessionVal }));
					}
					const userProfile = window.adobeIMS.getUserProfile();
					if (!isCCD) {
						initialState.isUserEntitled = isUserEligibleForCCHome;
						if (initialState.isUserEntitled) {
							// Start the profile fetch if the user is entitled.
							window.profileService = new ProfileService(store);
							window.profileService._fetchProfileFromGraphQL();
						}
					} else if (
						window.adobeIMS &&
						window.adobeIMS.getAccessToken() &&
						userProfile &&
						(userProfile.authId || userProfile.userId)
					) {
						window.profileService = new ProfileService(store);
						window.profileService._fetchProfileFromGraphQL();
					}

					const userCountry =
						initialState.profile &&
						initialState.profile.countryCode &&
						initialState.profile.countryCode.toLowerCase();
					const addFeatureFlagsFromActiveReleases = new FloodgateService().addFeatureFlagsFromActiveReleases(
						store.getState().app.language,
						userCountry,
					);

					// Get current users feature flags
					return Promise.all([FeaturesService.fetchFlags(store), addFeatureFlagsFromActiveReleases]);
				})
				.then(() => {
					// Fetch FFC and cache to sessionStorage
					if (window.cchome.util.hasFeature('ffc_prefetch')) {
						new SearchAppService().fetchProducts(true);
					}

					resolve(initialState);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}
}

export default function bootstrap(progressCallback, store, isCCD?: boolean) {
	// console.log(progressCallback);
	return new BootstrapService().getInitialState(progressCallback, store, isCCD);
}
